import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { NavRail, type NavPageId } from './NavRail';
import { CmdKProvider, CmdKPalette, useCmdK } from '../common/CmdKPalette';
import { TopActionBarActionsProvider } from '../../contexts/TopActionBarActionsContext';
import { AgentsUiProvider } from '../../contexts/AgentsUiContext';
import { RlmfUiProvider } from '../../contexts/RlmfUiContext';
import { VerifyUiProvider } from '../../contexts/VerifyUiContext';
import { DemoEngine } from '../../demo/DemoEngine';
import { DemoToastHost } from '../../demo/DemoToastHost';
import { useRealtimeInvalidation } from '../../hooks/useRealtimeInvestigation';
import { useLensParamShim } from '../../hooks/useLensParamShim';

/**
 * AppLayout — root shell.
 *
 * Cycle 102 sprint-1 Task 1.0: legacy global app shell RETIRED. The
 * Echelon top header (ShellHeader) and BUILD/INTELLIGENCE sidebar (Sidebar)
 * are gone — replaced by the 48px shared NavRail per the canonical design
 * handoff (`shared/navRail.jsx`). Theme state is managed here so NavRail's
 * footer toggle continues to work.
 *
 * Page-level chrome (Workspace's TopBar + InvestigationRail; Environment's
 * phase tabs) is owned by each page surface, not the global shell.
 */
export function AppLayout() {
  // WebSocket → TanStack Query bridge (platform-wide real-time updates)
  useRealtimeInvalidation('platform');
  // Cycle 101 sprint-2: URL-param rename shim. See SDD §5.1 D-101-7.
  useLensParamShim();
  const location = useLocation();
  const navigate = useNavigate();
  // CR-2: client-side navigation injected into the CmdK palette (⌘1/⌘2/⌘3 +
  // nav commands) so keyboard navigation doesn't full-page-reload the SPA.
  const handleNavigate = useCallback((path: string) => navigate(path), [navigate]);

  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    document.documentElement.classList.contains('light') ? 'light' : 'dark',
  );

  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    const root = document.documentElement;
    // Canonical class (cycle-109 v2 design-system.css selectors)
    root.classList.remove('theme-dark', 'theme-light');
    root.classList.add(next === 'light' ? 'theme-light' : 'theme-dark');
    // Legacy `.light` class continues to gate --e-* overrides in index.css
    if (next === 'light') {
      root.classList.add('light');
    } else {
      root.classList.remove('light');
    }
    // Persist per AC-1.1.b
    try {
      window.localStorage.setItem('echelon.workspace.theme', next);
    } catch {
      /* localStorage unavailable — non-fatal */
    }
  }

  const current: NavPageId | undefined = (() => {
    if (location.pathname.startsWith('/workspace') || location.pathname.startsWith('/fleet')) {
      return 'workspace';
    }
    if (location.pathname.startsWith('/environment') || location.pathname.startsWith('/graph')) {
      return 'environment';
    }
    if (location.pathname.startsWith('/market')) {
      return 'market';
    }
    return undefined;
  })();

  // Reset any stale state on route change (parity with prior AppLayout behavior).
  useEffect(() => {
    /* placeholder for cross-route side-effects; currently a no-op since
       mobile-menu state is gone with the legacy Sidebar. */
  }, [location.pathname]);

  return (
    <TopActionBarActionsProvider>
      <AgentsUiProvider>
        <RlmfUiProvider>
          <VerifyUiProvider>
            <DemoEngine>
              <DemoToastHost />
              {/* FR-S-1 (cycle-105.8): the ⌘K palette is hoisted from page scope
                  to the shell so it is genuinely shared across /workspace,
                  /environment, and /market. Provider-then-consumer: AppShell calls
                  useCmdK() to wire NavRail's ⌘K trigger, so the provider must wrap it. */}
              <CmdKProvider onNavigate={handleNavigate}>
                <AppShell
                  current={current}
                  pathname={location.pathname}
                  theme={theme}
                  onThemeChange={toggleTheme}
                />
              </CmdKProvider>
            </DemoEngine>
          </VerifyUiProvider>
        </RlmfUiProvider>
      </AgentsUiProvider>
    </TopActionBarActionsProvider>
  );
}

interface AppShellProps {
  current: NavPageId | undefined;
  pathname: string;
  theme: 'dark' | 'light';
  onThemeChange: () => void;
}

/**
 * Inner shell — consumes the CmdK context so NavRail's ⌘K trigger can open the
 * shell-mounted palette. Kept separate from AppLayout because `useCmdK()` must
 * resolve *below* `<CmdKProvider>` (FR-S-1 provider-then-consumer pattern).
 */
function AppShell({ current, pathname, theme, onThemeChange }: AppShellProps) {
  const { setOpen } = useCmdK();
  return (
    <div className="flex min-h-[100dvh] w-screen bg-[var(--e-bg-app)] text-[var(--e-text-primary)]">
      <NavRail
        current={current}
        theme={theme}
        onThemeChange={onThemeChange}
        onOpenCmdK={() => setOpen(true)}
      />

      <section className="relative flex min-h-[100dvh] min-w-0 flex-1 flex-col">
        <div className="relative flex-1 overflow-auto bg-[var(--e-bg-app)]">
          <div key={pathname} className="page-enter h-full">
            <Outlet />
          </div>
        </div>
      </section>

      {/* Shell-mounted command palette — renders DEFAULT_COMMANDS plus the
          per-surface extraCommands the active page registers via context (FR-S-2). */}
      <CmdKPalette />
    </div>
  );
}
